import React, { FunctionComponent, useContext, useMemo } from "react";
import {
    ContentSection,
    GridRow,
    GridColumn,
    ListTable,
    ButtonRenderProps,
    ButtonProps,
    ActionButton,
    Checkbox,
    DetailsRowProps,
    Grid,
    Stack,
    RenderFunction
} from "sprint-common-web";

import { TagElementsList } from "../../../../../common/components/TagElementsList";
import { renderNotMergableRow } from "../../../../common/utils/renderNotMergableRow";
import { TvSeriesDashboardContext } from "../../../TvSeriesDashboardProvider";
import { CreateEpisodeButton, CreateEpisodeButtonProps } from "./buttons/CreateEpisodeButton";
import { MergeEpisodesCommandBarButton } from "./buttons/MergeEpisodesCommandBarButton";
import { canEpisodesMerge } from "./canEpisodeMerge";
import { SeasonEpisodeListingContext } from "./context/SeasonEpisodeListingContext";
import { createEpisodeListingColumn } from "./createEpisodeListingColumns";
import { EpisodeListingItem } from "./EpisodeListingItem";

export const EpisodeListing: FunctionComponent = () => {
    const { selectCurrentEpisode } = useContext(TvSeriesDashboardContext);
    const {
        listingItems,
        episodesToMerge,
        filters,
        totalItems,
        setSort,
        setIncludeMerged,
        setPage,
        setPageSize,
        setOrphanedOnly,
        clearFilters,
        removeEpisodeFromMergeList
    } = useContext(SeasonEpisodeListingContext);

    const handleEpisodeSelected = (episode?: EpisodeListingItem) => {
        if (episode) {
            selectCurrentEpisode(episode?.id, episode?.title);
        }
    };
    const columns = useMemo(() => createEpisodeListingColumn(handleEpisodeSelected), [handleEpisodeSelected]);

    const handleOrphanedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setOrphanedOnly(checked ? true : undefined);
    };

    const handleIncludeMergedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setIncludeMerged(checked);
    };

    const renderRow = (rowProps?: DetailsRowProps, renderDefault?: RenderFunction<DetailsRowProps>) => {
        if (isRowMergable(episodesToMerge, rowProps?.item) && renderDefault) {
            return renderDefault(rowProps, renderDefault);
        }

        return renderNotMergableRow(rowProps, renderDefault);
    };

    return (
        <>
            <ContentSection header="Episodes" buttons={[mergeEpisodesButtonProps, createNewEpisodeButtonProps]}>
                <Grid>
                    <GridRow style={{ margin: 0 }}>
                        <TagElementsList
                            items={episodesToMerge}
                            onItemDismiss={removeEpisodeFromMergeList}
                            renderItemText={getMergeEpisodeText}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow style={{ margin: 0 }}>
                        <GridColumn sm={2}>
                            <Stack styles={{ root: { paddingTop: 5 } }}>
                                <Checkbox
                                    label="Orphaned Only"
                                    onChange={handleOrphanedCheckboxChange}
                                    checked={filters.orphanedOnly ?? false}
                                />
                            </Stack>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Stack styles={{ root: { paddingTop: 5 } }}>
                                <Checkbox
                                    label="Include Merge Sources"
                                    onChange={handleIncludeMergedCheckboxChange}
                                    checked={filters.includeMerged ?? false}
                                />
                            </Stack>
                        </GridColumn>
                        <GridColumn sm={8}>
                            <Stack horizontalAlign="end">
                                <ActionButton
                                    iconProps={{ iconName: "ClearFilter" }}
                                    ariaLabel="Clear filters"
                                    label="Clear filters"
                                    onClick={clearFilters}
                                >
                                    Clear filters
                                </ActionButton>
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                items={listingItems}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                onRenderRow={episodesToMerge.length ? renderRow : undefined}
                                isLoading={false}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const mergeEpisodesButtonProps: ButtonRenderProps<ButtonProps> = {
    render: MergeEpisodesCommandBarButton
};

const createNewEpisodeButtonProps: ButtonRenderProps<CreateEpisodeButtonProps> = {
    render: CreateEpisodeButton,
    isLabeled: true
};

const isRowMergable = (episodesToMerge: EpisodeListingItem[], rowItem?: EpisodeListingItem) => {
    return (
        (canEpisodesMerge(episodesToMerge[0], rowItem) && episodesToMerge.length < 2) ||
        episodesToMerge.some((episode: EpisodeListingItem) => episode.id === rowItem?.id)
    );
};

const getMergeEpisodeText = (listItem: EpisodeListingItem) => {
    return `${listItem.episodeNumber} - ${listItem.title}`;
};
